import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { api, ApiError } from "../api/client.js";

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { status, csrfToken, refresh, selectTenant } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<"pending" | "accepting" | "success" | "error">("pending");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== "authenticated" || token === null || token.length === 0) {
      return;
    }
    setState("accepting");
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.post<{ tenantId: string; role: string }>("/invitations/accept", csrfToken, { token });
        if (cancelled) {
          return;
        }
        await refresh();
        selectTenant(result.tenantId);
        setState("success");
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Could not accept the invitation — please try again.");
          setState("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, token]);

  if (token === null || token.length === 0) {
    return (
      <div className="login-shell">
        <div className="card login-card">
          <h2>Invitation link is missing its token</h2>
          <div className="auth-links">
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="login-shell">
        <div className="card login-card">
          <p className="muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return (
      <div className="login-shell">
        <div className="card login-card">
          <h2>Sign in to accept this invitation</h2>
          <p className="muted">You need an Emma OS account to accept this invitation. Sign in or sign up, then come back to this same link.</p>
          <div className="auth-links">
            <Link to={`/login?next=${encodeURIComponent(`/accept-invitation?token=${token}`)}`}>Sign in</Link> ·{" "}
            <Link to="/signup">Sign up</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="card login-card">
        <h2>Workspace invitation</h2>
        {(state === "pending" || state === "accepting") && <p className="muted">Accepting invitation…</p>}
        {state === "success" && (
          <>
            <div className="success-banner">Invitation accepted. You now have access to this workspace.</div>
            <button className="primary" style={{ width: "100%" }} onClick={() => navigate("/generate", { replace: true })}>
              Go to GENERATE
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="error-banner">{error}</div>
            <div className="auth-links">
              <Link to="/generate">Go to GENERATE</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
